// Shorts.jsx
import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel, Virtual } from 'swiper/modules';
import SwiperWithDefaultStyle from '../style/SwiperWithDefaultStyle.js';

import ShortsJsonLd from '../component/ShortsJsonLd.jsx';
import ShortsDetailJsonLd from '../container/ShortsDetailJsonLd.js';
import MessageHelmet from '../container/MessageHelmet.js';
import SeoHelmetWithTDK from '../component/SeoHelmetWithTDK.jsx';
import Redirect from '../container/Redirect.js';
import ShortMessage from '../container/ShortMessage.js';
import ShortSidebar from '../component/ShortSidebar.jsx';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import media from '../style/media.js';

const VIRTUAL_OFFSET = 1;
const PREFETCH_OFFSET = 3; // TODO: remote config
const MIN_DESKTOP_LAYOUT_HEIGHT = 900;
const isServer = typeof window === 'undefined';

const Shorts = ({
  deviceHeight,
  isOnDesktop,
  isNextPageFetching,
  isClipFirstPageFetching,
  isClipFirstPageFetched,
  isConfigMerged,
  shouldShowBack,
  lastWatchedShortIndex,
  messageIdFromUrl,
  feedId,
  clipFetchedFeedId,
  clipLatestFeedId,
  category,
  clipCategory,
  setLastWatchedIndex,
  fetchAndPrependMessageId,
  replace,
  fetchFeeds,
  itemIds,
  shortsSelectPath,
  nextPage,
  clipNextPage,
  clipLatestTimestamp,
  setFirstShortMessagePlayed,
  addMessageIdToShortWatchedList,
  prefetchedMessageAmount,
}) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const listPath = state?.listPath;
  const categoryRef = useRef(category);
  const swiperRef = useRef(null);
  const timeoutRef = useRef(null);
  const HelmetComponent = useMemo(() => {
    if (!messageIdFromUrl) {
      return (
        <>
          <SeoHelmetWithTDK
            title={t('seo_shorts_title', {
              ns: TranslationNamespace.SEO,
            })}
            description={t('seo_shorts_description', {
              ns: TranslationNamespace.SEO,
            })}
          />
          <ShortsJsonLd />
        </>
      );
    }
    return (
      <>
        <MessageHelmet
          messageId={messageIdFromUrl}
          pathnamePrefix={'shorts'}
          titleI18nKey={'seo_cp_shorts_title'}
          descriptionI18nKey={'seo_cp_shorts_description'}
        />
        <ShortsDetailJsonLd messageId={messageIdFromUrl} />
      </>
    );
  }, [messageIdFromUrl, t]);
  const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
  const [currentIndex, setCurrentIndex] = useState(lastWatchedShortIndex);

  // Fetch first page clip feeds when mount component.
  useEffect(() => {
    const isFeedIdChanged = !!(
      clipLatestFeedId && clipLatestFeedId != clipFetchedFeedId
    );
    const canFetch = !isClipFirstPageFetched && !isClipFirstPageFetching;
    if ((isFeedIdChanged || canFetch) && isConfigMerged) {
      fetchFeeds({
        page: 1,
        type: clipCategory,
        id: clipLatestFeedId,
        unixTimestamp: clipLatestTimestamp ? clipLatestTimestamp : undefined,
        shouldSkipLocalCache: !!clipLatestTimestamp,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prepend messageId from url to itemIds
  useEffect(() => {
    const isCategoryChanged = category !== categoryRef.current;
    const shouldPrependId = messageIdFromUrl && itemIds.length === 0;
    if (isCategoryChanged) {
      categoryRef.current = category;
    }

    if (listPath || !isConfigMerged) return;

    if (shouldPrependId || isCategoryChanged) {
      fetchAndPrependMessageId({
        category,
        messageId: messageIdFromUrl,
      });
    } else if (itemIds.length === 0) {
      fetchAndPrependMessageId({
        category,
      });
    }
  }, [
    isConfigMerged,
    messageIdFromUrl,
    category,
    shortsSelectPath,
    itemIds,
    listPath,
    fetchAndPrependMessageId,
  ]);

  // Replace url /shorts to /shorts/:messageId. Or replace to next messageId if lastWatchedShortIndex changed.
  useEffect(() => {
    const id = itemIds[lastWatchedShortIndex];
    const shouldReplaceToShortsDetail = id && id !== messageIdFromUrl;
    if (shouldReplaceToShortsDetail) {
      replace(`/shorts/${id}${window.location.search}`, {
        listPath,
        category,
        shouldShowBack,
      });
    }
  }, [
    messageIdFromUrl,
    lastWatchedShortIndex,
    shouldShowBack,
    itemIds,
    listPath,
    category,
    replace,
  ]);

  useEffect(() => {
    const messageId = itemIds[currentIndex];
    if (messageId) {
      addMessageIdToShortWatchedList({ messageId });
    }
  }, [currentIndex, itemIds, addMessageIdToShortWatchedList]);

  // Pre-fetch shorts next page data
  useEffect(() => {
    const shouldFetchNextPage =
      itemIds.length > 0 &&
      itemIds.length - PREFETCH_OFFSET <= lastWatchedShortIndex &&
      !isNextPageFetching;
    if (shouldFetchNextPage) {
      if (nextPage != null) {
        fetchFeeds({
          page: nextPage,
          type: category,
          isAllowSameId: true,
          selectPath: shortsSelectPath,
        });
      }
      if (clipNextPage != null) {
        fetchFeeds({
          page: clipNextPage,
          type: clipCategory,
        });
      }
    }
  }, [
    category,
    clipCategory,
    clipNextPage,
    fetchFeeds,
    isNextPageFetching,
    itemIds.length,
    lastWatchedShortIndex,
    nextPage,
    shortsSelectPath,
  ]);

  useEffect(() => {
    if (deviceHeight && swiperRef?.current) {
      swiperRef.current?.update();
    }
  }, [deviceHeight]);

  useEffect(() => {
    return () => {
      setFirstShortMessagePlayed({ hasPlayed: false });
    };
  }, [setFirstShortMessagePlayed]);

  const handleSlidePrev = useCallback(() => {
    swiperRef.current?.slidePrev();
  }, []);
  const handleSlideNext = useCallback(() => {
    swiperRef.current?.slideNext();
  }, []);

  if (!isServer && !messageIdFromUrl && !category && isConfigMerged) {
    return <Redirect to="/" status={302} />;
  }

  if (!itemIds?.length || !deviceHeight) {
    return (
      <StyledShorts
        deviceHeight={deviceHeight}
        isIphoneXSeriesPWA={isIphoneXSeriesPWA}
      >
        {HelmetComponent}
        <ShortMessage
          messageId={messageIdFromUrl}
          isActive={false}
          shouldShowBack={shouldShowBack}
          deviceHeight={deviceHeight}
        />
      </StyledShorts>
    );
  }

  const hasHeightEnough =
    deviceHeight && deviceHeight > MIN_DESKTOP_LAYOUT_HEIGHT;
  const shouldDisplaySidebar = isConfigMerged && itemIds?.length > 1;

  return (
    <StyledShorts
      deviceHeight={deviceHeight}
      isIphoneXSeriesPWA={isIphoneXSeriesPWA}
    >
      {HelmetComponent}
      <Container>
        <StyledSwiper
          modules={[Virtual, Keyboard, Mousewheel]}
          direction={'vertical'}
          initialSlide={lastWatchedShortIndex}
          onSwiper={swiper => {
            window.swiperRef = swiper;
            swiperRef.current = swiper;
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              return swiper.update();
            }, 0);
          }}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          simulateTouch={!isOnDesktop}
          virtual={{
            addSlidesBefore: VIRTUAL_OFFSET,
            addSlidesAfter: VIRTUAL_OFFSET,
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          mousewheel={{
            enabled: isOnDesktop,
            // invert: true,
            // sensitivity: 1,
            thresholdDelta: 90,
            // thresholdTime: 500,
          }}
          touchStartPreventDefault={false}
          onTransitionEnd={({ activeIndex: currentIndex }) => {
            if (feedId) {
              return setLastWatchedIndex({
                lastWatchedIndex: currentIndex,
                feedId,
              });
            }
          }}
          onSlideChange={({ activeIndex }) => {
            // Can't use `lastWatchedIndex` as counter, since `lastWatchedIndex` trigger onTransitionEnd,
            // it might miss the middle ids if index change very fast by tap or press hotkey
            setCurrentIndex(activeIndex);
          }}
          breakpoints={{
            768: {
              slidesPerView: hasHeightEnough ? 1.1 : 1,
              centeredSlides: true,
              spaceBetween: 36,
            },
          }}
        >
          {itemIds.map((messageId, index) => (
            <SwiperSlide key={index}>
              <ShortMessage
                key={messageId}
                messageId={messageId}
                isActive={index === lastWatchedShortIndex}
                shouldShowBack={shouldShowBack}
                deviceHeight={deviceHeight}
                shouldPrefetch={
                  // If there is any Short that has been activated (lastWatchedShortIndex >= 0),
                  // prefetch the segments for activated one and the next two (if any)
                  lastWatchedShortIndex >= 0 &&
                  index >= lastWatchedShortIndex &&
                  index <= lastWatchedShortIndex + prefetchedMessageAmount
                }
              />
            </SwiperSlide>
          ))}
        </StyledSwiper>

        {shouldDisplaySidebar && (
          <ShortSidebar
            isOnFirst={lastWatchedShortIndex === 0}
            isOnLast={lastWatchedShortIndex === itemIds.length - 1}
            slideNext={handleSlideNext}
            slidePrev={handleSlidePrev}
          />
        )}
      </Container>
    </StyledShorts>
  );
};

Shorts.propTypes = {
  messageIdFromUrl: PropTypes.string,
  feedId: PropTypes.string,
  clipFetchedFeedId: PropTypes.string,
  clipLatestFeedId: PropTypes.string,
  category: PropTypes.string,
  clipCategory: PropTypes.string,
  deviceHeight: PropTypes.number,
  isOnDesktop: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isClipFirstPageFetching: PropTypes.bool,
  isClipFirstPageFetched: PropTypes.bool,
  isConfigMerged: PropTypes.bool,
  shouldShowBack: PropTypes.bool,
  lastWatchedShortIndex: PropTypes.number,
  prefetchedMessageAmount: PropTypes.number,
  setLastWatchedIndex: PropTypes.func,
  fetchAndPrependMessageId: PropTypes.func,
  replace: PropTypes.func,
  fetchFeeds: PropTypes.func,
  itemIds: PropTypes.array,
  shortsSelectPath: PropTypes.array,
  nextPage: PropTypes.number,
  clipNextPage: PropTypes.number,
  clipLatestTimestamp: PropTypes.number,
  setFirstShortMessagePlayed: PropTypes.func,
  addMessageIdToShortWatchedList: PropTypes.func,
};

Shorts.defaultProps = {
  messageIdFromUrl: null,
  feedId: null,
  clipFetchedFeedId: null,
  clipLatestFeedId: null,
  category: null,
  clipCategory: null,
  deviceHeight: null,
  isOnDesktop: false,
  isNextPageFetching: false,
  isClipFirstPageFetching: false,
  isClipFirstPageFetched: false,
  isConfigMerged: false,
  shouldShowBack: false,
  lastWatchedShortIndex: 0,
  prefetchedMessageAmount: 2,
  setLastWatchedIndex: () => null,
  fetchAndPrependMessageId: () => null,
  replace: () => null,
  fetchFeeds: () => null,
  itemIds: [],
  shortsSelectPath: [],
  nextPage: null,
  clipNextPage: null,
  clipLatestTimestamp: null,
  setFirstShortMessagePlayed: () => null,
  addMessageIdToShortWatchedList: () => null,
};

const StyledShorts = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
  height: ${({ deviceHeight }) =>
    deviceHeight ? `${deviceHeight}px` : '100vh'};
  padding-top: ${headerHeight}px;
  ${media.mobile`
    padding-top: 0;
    padding-bottom: 0;
    height: ${({ deviceHeight, isIphoneXSeriesPWA }) =>
      isIphoneXSeriesPWA
        ? `${
            deviceHeight -
            footerNavigationHeight -
            iPhoneXSeriesFooterPaddingInPWA
          }px`
        : `${deviceHeight - footerNavigationHeight}px`};
  `};
`;

const StyledSwiper = styled(SwiperWithDefaultStyle)`
  .swiper-wrapper {
    display: flex;
    flex-direction: column;
  }
`;

const Container = styled.div`
  position: relative;
  display: inline-block;
  height: 100%;
  width: auto;
  ${media.mobile`
    width: 100%;
  `}
`;

export default Shorts;
